import { BOOLEAN_RADIO_INPUT_OPTIONS } from "@constants/boolean-radio-input-options";
import { resources } from "@constants/hardcoded-strings";
import { INCODE_SMS_DOC_CAPTURE_SO_NAME_PROPERTY_ID, INCODE_SMS_USE_IDW_TO_SEND_PROPERTY_ID, INCODE_SMS_MESSAGING_SERVICE_OPTIONS } from "@constants/incode-sms-constants";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body"
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header"
import { PropertiesSectionDescription } from "@containers/common/properties-sections/properties-section-header/properties-section-description"
import { RadioInputFormGroup } from "@containers/common/radio-input-form-group/radio-input-form-group";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { PageLevelTypes } from "@enums/page-level-types";
import { SelectOption } from "@iddataweb/idw-common-ui/types";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { FormattedAppliedProfile } from "@idw-types/data/applied-profile/applied-profile";
import { AxnDocumentationLink } from "@idw-types/data/reference-data/axn";
import { FormattedRpServiceConfigurationProperty } from "@idw-types/data/rp-service-configuration";
import { getIncodeSmsInfo } from "@utils/incode-sms/incode-sms-util";
import { useMemo } from "react";
import styles from "./incode-sms-container.less";

type IncodeSMSContainerProps = {
    isDisabled: boolean;
    appliedProfiles: Array<FormattedAppliedProfile>;
    properties: Array<FormattedRpServiceConfigurationProperty>;
    documentationLinks: Array<AxnDocumentationLink>;
    pageLevel: PageLevelTypes;
    workflowId: string;
    canModifyNonRestrictedProperties: boolean;
    incodeSmsMessagingServiceError: React.ReactNode;
    updateProperties: (properties: Array<{ propertyTypeId: string; newValue: string; added: boolean; }>) => void;
}

export const IncodeSMSContainer = (props: IncodeSMSContainerProps) => {
    const { documentationLinks, appliedProfiles,
        properties, pageLevel, workflowId, canModifyNonRestrictedProperties,
        updateProperties, incodeSmsMessagingServiceError
    } = props;

    const onChangeToggleIncodeSms = (value: string) => {
        const isEnabled = isStringTrue(value);

        updateProperties([
            { propertyTypeId: INCODE_SMS_USE_IDW_TO_SEND_PROPERTY_ID, newValue: value, added: isEnabled },
            { propertyTypeId: INCODE_SMS_DOC_CAPTURE_SO_NAME_PROPERTY_ID, newValue: (!isEnabled ? null : INCODE_SMS_MESSAGING_SERVICE_OPTIONS[0].value), added: isEnabled }
        ]);
    }

    const handleDocCaptureSoNameChange = (selectedOption: SelectOption<string>) => {
        updateProperties([
            { propertyTypeId: INCODE_SMS_DOC_CAPTURE_SO_NAME_PROPERTY_ID, newValue: selectedOption.value, added: true }
        ]);
    }

    const incodeSmsInfo = useMemo(() => {
        return getIncodeSmsInfo(properties, appliedProfiles);
    }, [properties, appliedProfiles]);

    const inheritedIncodeSmsInfo = useMemo(() => {
        return getIncodeSmsInfo([], appliedProfiles);
    }, [appliedProfiles]);

    const documentationLink = useMemo(() => {
        if (documentationLinks.length) {
            const documentationLink = documentationLinks.find(doc => doc.shortName === '');// todo: need to set the doc link
            if (documentationLink) {
                return documentationLink.url;
            }
        }

        return '';
    }, [documentationLinks]);

    const isServiceWithoutWorkflow = useMemo(() => {
        return pageLevel === PageLevelTypes.APIKEY && !workflowId;
    }, [pageLevel, workflowId]);

    const selectedMessagingServiceOption = useMemo(() => {
        const option = INCODE_SMS_MESSAGING_SERVICE_OPTIONS.find(messagingService => messagingService.value === incodeSmsInfo.docCaptureSoNamePropertyValue);
        if (option) {
            return option;
        }

        return null;
    }, [INCODE_SMS_MESSAGING_SERVICE_OPTIONS, incodeSmsInfo.docCaptureSoNamePropertyValue]);

    const isDisabled = useMemo(() => {
        return (
            props.isDisabled ||
            (pageLevel === PageLevelTypes.APIKEY && !!workflowId) ||
            !canModifyNonRestrictedProperties
        );
    }, [props.isDisabled, pageLevel, workflowId, canModifyNonRestrictedProperties]);


    const incodeSmsResources = resources.pages.verificationServiceDetails.tabs.general.subTabs.advanced.sections.incodeSms;

    return (<div>
        <ContentPageHeader title={incodeSmsResources.title} />
        <ContentPageBody isMaxWidth={true}>
            <PropertiesSectionDescription description={incodeSmsResources.descriptionText} documentationLink={documentationLink} />
            <div className={styles.incodeSmsContent}>
                <ThemingFormGroupWrapper
                    currentThemingPropertyValue={incodeSmsInfo.isIncodeSmsEnabled}
                    inheritedThemingPropertyValue={inheritedIncodeSmsInfo.isIncodeSmsEnabled}
                    pageLevel={pageLevel}
                    hideComparison={isServiceWithoutWorkflow || pageLevel === PageLevelTypes.WORKFLOW}
                >
                    <RadioInputFormGroup
                        controlId={'enable-incode-sms'}
                        automationId={'enable-incode-sms'}
                        disabled={isDisabled}
                        isRequired={false}
                        label={incodeSmsResources.inputs.enableIncodeSms.label}
                        layout={LayoutTypes.COLUMN}
                        labelWidth={LabelWidthTypes.LARGE}
                        value={incodeSmsInfo.isIncodeSmsEnabled
                            ? BOOLEAN_RADIO_INPUT_OPTIONS[0].value
                            : BOOLEAN_RADIO_INPUT_OPTIONS[1].value
                        }
                        options={BOOLEAN_RADIO_INPUT_OPTIONS}
                        onChange={onChangeToggleIncodeSms}
                    />
                </ThemingFormGroupWrapper>

                {incodeSmsInfo.isIncodeSmsEnabled && <div>
                    <ThemingFormGroupWrapper
                        currentThemingPropertyValue={incodeSmsInfo.docCaptureSoNamePropertyValue}
                        inheritedThemingPropertyValue={inheritedIncodeSmsInfo.docCaptureSoNamePropertyValue}
                        pageLevel={pageLevel}
                        hideComparison={isServiceWithoutWorkflow || pageLevel === PageLevelTypes.WORKFLOW}
                    >
                    <SelectFormGroup
                        controlId={"incode-sms-messaging-service-dropdown"}
                        automationId={"incode-sms-messaging-service-dropdown"}
                        label={incodeSmsResources.inputs.messagingService.label}
                        ariaLabel={incodeSmsResources.inputs.messagingService.label}
                        options={INCODE_SMS_MESSAGING_SERVICE_OPTIONS}
                        selectedOption={selectedMessagingServiceOption}
                        layout={LayoutTypes.COLUMN}
                        labelWidth={LabelWidthTypes.LARGE}
                        isClearable={false}
                        isSearchable={false}
                        disabled={isDisabled}
                        isRequired={true}
                        onChange={handleDocCaptureSoNameChange}

                    />
                      </ThemingFormGroupWrapper>
                    {incodeSmsMessagingServiceError}

                </div>}
            </div>

        </ContentPageBody>
    </div>)
}